<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2022-05-06 16:25:52
 * @LastEditors: luckymiaow
-->

<script setup lang="ts">
import { Popconfirm as APopconfirm, Spin as ASpin, Tree as ATree, message } from 'ant-design-vue';
import { computed, getCurrentInstance, nextTick, onMounted, ref, unref, useAttrs, useSlots, watchEffect } from 'vue';
import { CheckOutlined, CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons-vue';
import type { EventDataNode, FieldNames } from 'ant-design-vue/lib/vc-tree/interface';
import type { AntTreeNodeDragEnterEvent } from 'ant-design-vue/lib/tree';
import type { BaseTree, IBaseTreeOptions, TreeNode } from '../types';
import { OperatingType, TreeNodeSlotsNode } from '../types';
import { deepCopy } from '../../util';
import useConfigProvider from '../../config-provider/src/useConfigProvider';
import useSlotsHooks from '../../hooks/useSlot';
import { DefaultOptions, ObjectMap, fetchTreeNode, loopEditNode, mapTreeNode } from './useTree';
const props = withDefaults(defineProps<Props>(), {
  options: props => new DefaultOptions() as IBaseTreeOptions<unknown>,
  expandedKeys: () => [],
});
const emit = defineEmits(['change', 'update:expandedKeys', 'select']);

interface Props extends BaseTree {
  options?: IBaseTreeOptions<unknown>;
  loadData?(node: { dataRef: TreeNodeSlotsNode<unknown> } | EventDataNode): Promise<void>;
  treeData?: TreeNode<unknown>[];
  expandedKeys?: string[] | number[];
  ellipsis?: boolean; // 省略标题
  fieldNames?: FieldNames;
}

const attrs = useAttrs();

const { useExcludeSlots } = useSlotsHooks({ slots: useSlots() }, ['default', 'title']);
const injectConfig = useConfigProvider().injectConfig<BaseTree>('baseTree');

const getBindValue = computed<BaseTree>(() => ({
  ...injectConfig?.value,
  ...attrs,
  loadData: props.options.findChild
    ? (e: { dataRef: TreeNodeSlotsNode<unknown> }) => loadData(e.dataRef) as any
    : (null as any),
  treeData: mappedData.value,
  draggable: props.options.canMove,
}));

const _expandedKeys = ref<string[] | number[]>([]);
function expandedChange(e: never[]) {
  _expandedKeys.value = e;
  emit('update:expandedKeys', _expandedKeys.value);
}

onMounted(() => {
  _expandedKeys.value = props.expandedKeys;
});

const editData = ref(new TreeNodeSlotsNode(null, null));

const mappedData = ref<TreeNodeSlotsNode<unknown>[]>([]);

watchEffect(() => {
  const data = props.treeData ?? [];
  mappedData.value = deepCopy(mapTreeNode(data, null));
});

const showToolKey = ref<string | null>(null);

let operatingType = ref<'添加' | '编辑'>('添加');

const inputRef = ref<HTMLInputElement>();

const editSpin = ref(false);

/**
 * @description: 编辑时禁用其它操作
 * @param {boolean} enabled
 * @return {*}
 * @author: luckymiaow
 */
const backOptions = ref<(IBaseTreeOptions<unknown> & { node: TreeNodeSlotsNode<unknown> }) | null>(null);
const disabled = (on = false, node?: TreeNodeSlotsNode<unknown>) => {
  if (on == true) {
    backOptions.value = {
      canMove: props.options?.canMove,
      node: deepCopy(node) as any,
    };
    if (node != null) node.disabled = true;

    if (props.options) props.options.canMove = false;
  } else {
    if (node != null) node.disabled = backOptions.value?.node.disabled ?? false;

    if (props.options) props.options.canMove = backOptions.value?.canMove ?? false;
  }
  mappedData.value = [...mappedData.value];
};

/**
 * @description: 添加节点
 * @param {*} item
 * @return {*}
 * @author: luckymiaow
 */
const addTreeNode = async (dataRef: TreeNodeSlotsNode<unknown>) => {
  if (editData.value.on) return message.warning('请完成当前操作！');
  operatingType.value = '添加';
  editData.value.on = true;
  showToolKey.value = '';
  if (dataRef) {
    if (dataRef.childCount > 0) {
      if (getBindValue.value?.loadData != null) getBindValue.value.loadData({ dataRef });
    }
    editData.value = new TreeNodeSlotsNode(null, dataRef);
    editData.value.on = true;
    dataRef.children.push(editData.value);
    dataRef.childCount++;
    if (!_expandedKeys.value?.includes(dataRef.key as never)) _expandedKeys.value.push(dataRef.key as never);
  } else {
    editData.value = new TreeNodeSlotsNode(null, null);
    editData.value.on = true;
    mappedData.value.push(editData.value);
  }
  mappedData.value = [...mappedData.value];
  await nextTick();
  inputRef.value?.focus();
  disabled(true, editData.value);
};

const saveNode = async (node: TreeNodeSlotsNode<unknown>) => {
  if (!editData.value.title) return message.warning('不能为空');
  if (operatingType.value == '添加') await createNode(node);
  if (operatingType.value == '编辑') await updateNode(node);
  disabled(false, node);
};

const cancelNode = (node: TreeNodeSlotsNode<unknown>) => {
  if (operatingType.value == '添加') {
    delLocaNode(node);
  }
  editData.value = new TreeNodeSlotsNode(null, null);
  disabled(false, node);
};

const delLocaNode = async (dataRef: TreeNodeSlotsNode<unknown>) => {
  if (dataRef.parent == null) {
    mappedData.value = mappedData.value.filter(e => e.key != dataRef.key);
  } else {
    dataRef.parent.children = dataRef.parent.children.filter(e => e.key != dataRef.key);
    dataRef.parent.childCount--;
  }
};

/**
 * @description: 创建节点
 * @param {*}
 * @return {*}
 * @author: luckymiaow
 */
const createNode = async (node: TreeNodeSlotsNode<unknown>) => {
  editSpin.value = true;
  try {
    if (!props.options?.addChildAsync) throw new Error('addChildAsync is NULL');
    let children = mappedData.value;
    if (node.parent) children = node.parent.children;
    let directory = node.parent?.value ? node.parent?.value : null;
    if (children.some(e => e.title == editData.value.title && !e.on)) {
      editSpin.value = false;
      throw new Error('节点名称重复');
    }
    let n = await props.options.addChildAsync(directory!, editData.value.title, node.parent);
    node.key = n.key;
    node.value = n.value!;
    node.title = n.title as string;
    node.data = n?.data;
    editData.value = new TreeNodeSlotsNode(null, null);
    change(node, OperatingType.add);
    editSpin.value = false;
  } catch (err: any) {
    editSpin.value = false;
    message.error(`创建节点失败：${err.message}`);
  }
};

/**
 * @description: 更新节点
 * @param {*}
 * @return {*}
 * @author: luckymiaow
 */
const updateNode = async (node: TreeNodeSlotsNode<unknown>) => {
  editSpin.value = true;
  try {
    if (!props.options?.renameAsync) throw new Error('renameAsync is NULL');

    // 更新节点api
    const _editData = unref(editData);
    let res = await props.options.renameAsync(node, _editData.title);

    let old = _editData.value;

    let values = _editData.value.split('/');

    values = values.splice(0, values.length - 1);

    _editData.value = [...values, _editData.title].join('/');

    // if (res == 'local') {
    // }
    await loopEditNode(node.children, old, _editData.value, res);
    ObjectMap(_editData, node);
    editData.value = new TreeNodeSlotsNode(null, null);
    change(node, OperatingType.rename);
    editSpin.value = false;
  } catch (err: any) {
    editSpin.value = false;
    message.error(`更新节点失败：${err.message}`);
  }
};

/**
 * @description: 编辑节点
 * @param {*} node
 * @return {*}
 * @author: luckymiaow
 */
const editTreeNode = async (dataRef: TreeNodeSlotsNode<unknown>) => {
  disabled(true, dataRef);
  operatingType.value = '编辑';
  editData.value = deepCopy(dataRef);
  editData.value.on = true;
  await nextTick();
  inputRef.value?.focus();
};

/**
 * @description: 删除节点
 * @param {*} node
 * @return {*}
 * @author: luckymiaow
 */
const delTreeNode = async (dataRef: TreeNodeSlotsNode<unknown>) => {
  editSpin.value = true;
  try {
    if (!props.options?.deleteAsync) throw new Error('deleteAsync is NULL');

    let node = await props.options.deleteAsync(dataRef);
    delLocaNode(dataRef);
    change(dataRef, OperatingType.del);
    message.success('删除节点成功');
    editSpin.value = false;
  } catch (err: any) {
    editSpin.value = false;
    message.error(`删除节点失败：${err.message}`);
  }
};

/**
 * @description: 选择树节点
 * @param {*} item
 * @return {*}
 * @author: luckymiaow
 */
const select = (selectedKeys: any, e: { selected: boolean; selectedNodes: any; node: any; event: any }) => {
  let node = e.node;
  showToolKey.value = e.selected ? node.dataRef.key : null;
  emit('select', selectedKeys, e);
};

const change = (data: TreeNodeSlotsNode<unknown>, type: OperatingType) => {
  emit('change', data.node, { type, treeData: fetchTreeNode(mappedData.value) });
};

const loadData = async (dataRef: TreeNodeSlotsNode<unknown>) => {
  return new Promise<void>(async (resolve, reject) => {
    try {
      if (!props.options?.findChild) throw new Error('findChild is NULL');
      const data = await props.options.findChild(dataRef.node, 1);
      dataRef.children = mapTreeNode(data, dataRef);
      dataRef.childCount = dataRef.children.length;
      mappedData.value = [...mappedData.value];
      resolve();
    } catch (e) {
      reject(e);
    }
  });
};

const onDrop = async (info: {
  dropPosition: number;
  dropToGap: boolean;
  node: { dataRef: TreeNodeSlotsNode<unknown>; pos: string; expanded: any };
  dragNode: { dataRef: TreeNodeSlotsNode<unknown>; pos: any; children: any; expanded: any }; // 拖拽的节点
}) => {
  const dropKey = info.node.pos;
  const dragKey = info.dragNode.pos;
  const dropPos = info.node.pos.split('-');
  const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
  const data = mappedData.value;
  let dragParentChildren = info.dragNode.dataRef.parent?.children ?? mappedData.value;
  let nodeParentChildren = info.node.dataRef.parent?.children ?? mappedData.value;
  let dragIndex = dragParentChildren.findIndex(e => e.key == info.dragNode.dataRef.key);
  let nodeIndex = nodeParentChildren.findIndex(e => e.key == info.node.dataRef.key);

  const moveDirectory = (nPChildren = nodeParentChildren, node?: TreeNodeSlotsNode<unknown>) => {
    let item = dragParentChildren.splice(dragIndex, 1)[0];
    let i = nodeParentChildren.findIndex(e => e.key == info.node.dataRef.key);

    if (node) {
      info.dragNode.dataRef.value = `${node.value}/${info.dragNode.dataRef.title}`;
      info.dragNode.dataRef.parent = node; // 更新父节点
    } else {
      info.dragNode.dataRef.value =
        info.node.dataRef.parent == null
          ? info.dragNode.dataRef.title
          : `${info.node.dataRef.value}/${info.dragNode.dataRef.title}`; // 更新渲染树的目录
      info.dragNode.dataRef.parent = info.node.dataRef.parent; // 更新父节点
      if (dropPosition == 1) i = i + 1;
    }

    nPChildren.splice(i, 0, item);

    if (info.dragNode.dataRef.parent) {
      info.dragNode.dataRef.parent.childCount--;
      if (info.dragNode.dataRef.parent.childCount == 0) info.dragNode.dataRef.parent.isLeaf = true;
    }
    if (info.node.dataRef.parent) {
      info.node.dataRef.childCount++;
      info.node.dataRef.isLeaf = false;
    }
  };
  if (!props.options?.moveAsync) throw new Error('moveAsync is NULL');
  if (!props.options?.SortMoveBefore) throw new Error('SortMoveBefore is NULL');

  if (info.dropToGap === false) {
    // 移入目录

    await props.options.moveAsync(info.dragNode.dataRef.node, info.node.dataRef);

    moveDirectory(info.node.dataRef.children, info.node.dataRef);
  } else if (dragKey.slice(0, dragKey.lastIndexOf('-')) != dropKey.slice(0, dropKey.lastIndexOf('-'))) {
    // 移动到外层目录
    if (nodeIndex > -1) {
      await props.options.moveAsync(info.dragNode.dataRef.node, info.node.dataRef.parent);
      await props.options.SortMoveBefore(info.dragNode.dataRef.key, nodeParentChildren[nodeIndex]?.key ?? null);
      moveDirectory();
    }
  } else {
    if (dropPosition === -1) {
      await props.options.SortMoveBefore(info.dragNode.dataRef.key, nodeParentChildren[nodeIndex]?.key ?? null);
      let item = dragParentChildren.splice(dragIndex, 1)[0];
      nodeIndex = nodeParentChildren.findIndex(e => e.key == info.node.dataRef.key);
      nodeParentChildren.splice(nodeIndex, 0, item);
    } else {
      await props.options.SortMoveBefore(info.dragNode.dataRef.key, nodeParentChildren[nodeIndex + 1]?.key ?? null);
      let item = dragParentChildren.splice(dragIndex, 1)[0];
      nodeIndex = nodeParentChildren.findIndex(e => e.key == info.node.dataRef.key);
      nodeParentChildren.splice(nodeIndex + 1, 0, item);
    }
  }
  mappedData.value = [...data];
};

const onDragEnter = (info: AntTreeNodeDragEnterEvent) => {
  // expandedKeys 需要展开时
  // expandedKeys.value = info.expandedKeys;
};

const fetchTree = computed(() => fetchTreeNode(mappedData.value));

defineExpose({
  addTreeNode,
  treeData: fetchTree,
});

const treenodeWidth = ref('100%');

onMounted(() => {
  const width = getCurrentInstance()?.proxy?.$el?.nextSibling?.clientWidth;
  treenodeWidth.value = width ? `${width}px` : '100%';
});
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<template>
  <a-tree
    v-bind="getBindValue"
    :expanded-keys="_expandedKeys"
    @select="select"
    @drop="onDrop"
    @dragenter="onDragEnter"
    @update:expanded-keys="expandedChange"
  >
    <template v-for="(slot, key) in useExcludeSlots" :key="key" #[key]="scope">
      <slot :name="key" v-bind="scope ?? {}"> </slot>
    </template>
    <template #title="item">
      <span v-if="editData.key == item.dataRef.key && item.dataRef.isEdit" class="tools-slot" @click.stop>
        <a-spin :spinning="editSpin">
          <div class="spin-content">
            <input ref="inputRef" v-model="editData.title" class="tree-input" />
            <div class="tools-btn">
              <a title="确认" @click="saveNode(item.dataRef)"><CheckOutlined /></a>
              <div></div>
              <a style="color: red" title="取消" @click="cancelNode(item.dataRef)"><CloseOutlined /></a>
            </div>
          </div>
        </a-spin>
      </span>
      <div v-else class="tools-slot">
        <div class="title" :class="{ ellipsis: props.ellipsis }">
          <slot name="title" v-bind="item">
            {{ item.dataRef.title ? item.dataRef.title : '未命名' }}
          </slot>
        </div>
        <span
          v-show="item.dataRef.key == showToolKey"
          v-if="editData.on == false && item.dataRef.isEdit"
          class="tools"
          :class="[editData && editData.key != item.dataRef.key ? 'toolsShow' : '']"
          style="margin-left: 10px"
        >
          <slot
            name="tools"
            :edit-tree-node="{ fn: editTreeNode, param: item.dataRef }"
            :add-tree-node="{ fn: addTreeNode, param: item.dataRef }"
            :del-tree-node="{ fn: delTreeNode, param: item.dataRef }"
          ></slot>
          <slot
            v-if="options.canRename || item.dataRef.editBtn"
            name="editBtn"
            :fn="editTreeNode"
            :param="item.dataRef"
            :node="item.dataRef"
            ><EditOutlined v-if="options.canRename" style="color: #52c41a" @click="editTreeNode(item.dataRef)"
          /></slot>
          <slot
            v-if="options.canAddChild || item.dataRef.addBtn"
            name="addBtn"
            :fn="addTreeNode"
            :param="item.dataRef"
            :node="item.dataRef"
          >
            <PlusOutlined v-if="options.canAddChild" style="color: #1890ff" @click="addTreeNode(item.dataRef)" />
          </slot>
          <slot
            v-if="options.canDelete || item.dataRef.delBtn"
            name="delBtn"
            :fn="delTreeNode"
            :param="item.dataRef"
            :node="item.dataRef"
          >
            <a-popconfirm
              v-if="options.canDelete"
              title="确定删除吗？"
              ok-text="确定"
              cancel-text="取消"
              @confirm="delTreeNode(item.dataRef)"
            >
              <CloseOutlined style="color: red" />
            </a-popconfirm>
          </slot>
        </span>
      </div>
    </template>
  </a-tree>
</template>

<style scoped lang="less">
.tools {
  color: #000;
  white-space: nowrap;
  .anticon {
    margin-left: 5px;
  }
}

.tools-slot {
  display: flex;
  width: 100%;
  .title {
  }
  .ellipsis {
    flex: 1;
    width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .tree-input {
    outline-style: none;
    outline-color: invert;
    text-shadow: none;
    padding: 0 5px;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
  }
}
.tools-slot:hover .tools {
  display: inline !important;
}
.spin-content {
  border: 1px solid #91d5ff;
  background-color: #e6f7ff;
  display: flex;
  align-items: center;
  .tree-input {
    color: #000;
  }
  .tools-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 6px;
    > div {
      border: 1px solid #ddd;
      height: 11px;
    }
  }
}
</style>

<style>
.ant-tree {
  width: 100%;
}
.ant-tree .ant-tree-treenode {
  width: v-bind(treenodeWidth);
}
.ant-tree .ant-tree-node-content-wrapper {
  flex: auto;
  display: flex;
}
.ant-tree-title {
  flex: 1;
}
</style>
